import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ArrowRight, Calendar, Loader } from 'lucide-react';
import { functions } from '../../firebase';

export default function CheckoutSuccess({ companyId }) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(Boolean(companyId));
  const [refreshError, setRefreshError] = useState('');

  useEffect(() => {
    if (!companyId) {
      setRefreshing(false);
      return;
    }

    let cancelled = false;

    const refreshStatus = async () => {
      try {
        const refreshSubscriptionStatus = httpsCallable(functions, 'shifts_refreshSubscriptionStatus');
        await refreshSubscriptionStatus({ companyId });
        if (!cancelled) {
          setRefreshError('');
        }
      } catch (err) {
        console.error('Checkout success refresh error:', err);
        if (!cancelled) {
          setRefreshError(t('subscription.checkoutSuccessSyncWarning', 'Your payment was received, but subscription access is still syncing. Please use the refresh button below if the dashboard stays locked.'));
        }
      } finally {
        if (!cancelled) {
          setRefreshing(false);
        }
      }
    };

    refreshStatus();

    return () => {
      cancelled = true;
    };
  }, [companyId, t]);

  const handleRefresh = async () => {
    if (!companyId) return;
    setRefreshing(true);
    setRefreshError('');
    try {
      const refreshSubscriptionStatus = httpsCallable(functions, 'shifts_refreshSubscriptionStatus');
      await refreshSubscriptionStatus({ companyId });
    } catch (err) {
      console.error('Manual subscription refresh error:', err);
      setRefreshError(t('subscription.refreshStatusFailed', 'We could not refresh your subscription status yet. Please wait a moment and try again.'));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={44} className="text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('subscription.checkoutSuccessTitle', 'Subscription Active!')}</h1>
        <p className="text-gray-600 mb-8">
          {t('subscription.checkoutSuccessBody', 'Thank you for subscribing to BuildMyShifts. Your subscription is now active and you have full access to all features.')}
        </p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Calendar size={20} className="text-primary" />
            <span className="font-medium text-gray-900">BuildMyShifts</span>
          </div>
          <p className="text-sm text-gray-500">
            {t('subscription.checkoutSuccessRenewal', 'Your subscription will auto-renew monthly. You can manage or cancel your subscription at any time from Settings.')}
          </p>
        </div>

        {refreshing && (
          <div className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500">
            <Loader size={16} className="animate-spin" />
            {t('subscription.syncingAccess', 'Syncing your subscription access...')}
          </div>
        )}

        {refreshError && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            {refreshError}
          </div>
        )}

        {companyId && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mb-4 inline-flex items-center gap-2 border border-primary text-primary px-5 py-2.5 rounded-xl font-medium hover:bg-primary/5 transition-colors disabled:opacity-50"
          >
            {refreshing ? <Loader size={16} className="animate-spin" /> : null}
            {t('subscription.refreshAccessShort', 'Refresh Access')}
          </button>
        )}

        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-colors"
        >
          {t('subscription.goToDashboard', 'Go to Dashboard')} <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}
